import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-20 py-32 text-center">
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">Back to Home</Link>
        </div>
      </main>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    publisher: {
      '@type': 'Organization',
      name: 'pandabuy.monster',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <article className="pt-20">
        {/* Article Header */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
                  In-Depth Guide
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-foreground-950 leading-tight mb-6">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 mb-6">
                  <img
                    src={article.authorAvatar}
                    alt={article.authorName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{article.authorName}</p>
                    <p className="text-xs text-foreground-500">Senior Curator &bull; {article.date}</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-accent-500 rounded-2xl p-8 md:p-10 sticky top-24">
                  <h3 className="text-white/70 text-xs uppercase tracking-wider mb-3">Quick Summary</h3>
                  <p className="text-white/90 text-sm leading-relaxed mb-6">
                    {article.description}
                  </p>
                  <div className="flex flex-col gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/15 text-white text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
                    >
                      Explore Spreadsheets
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-accent-700 text-sm font-medium hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Back to Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Image */}
        <section className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-4">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-64 md:h-96 object-cover object-top rounded-2xl"
            />
          </div>
        </section>

        {/* Article Body */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom">
              {article.content.map((section, idx) => (
                <div key={idx}>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4 mt-10 first:mt-0">
                    {section.heading}
                  </h2>
                  <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
                    {section.body}
                  </p>
                </div>
              ))}

              <div className="border-t border-background-200 pt-8 mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Return to pandabuy.monster Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-accent-300 text-accent-700 text-sm font-medium hover:bg-accent-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}